/**
 * Web implementation of the shared-core PlatformCapabilities port.
 *
 * This is the only file in apps/frontend outside components/primitives/
 * that is allowed to touch browser globals directly. The portability check
 * (scripts/check_frontend_portability.py) enforces this.
 */
import type {
  GlobalKeyEvent,
  PasteContents,
  PlatformCapabilities,
} from "@shared-core/capabilities/ports";
import { firstPastedImage } from "@shared-core/domain/object-ui/upload-field";

/** Hand a viewer tab an object URL for already-fetched bytes.
 *
 * The blob carries its own media type, so the browser picks its viewer from
 * that — which is why the binary path must never round-trip through a string
 * (see `openBlobInNewTab` on the port). */
function openObjectUrlInNewTab(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
  // The opened tab references the object URL; revoke after a grace period so
  // it isn't leaked but the tab has had time to load it.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// Below this, a gap between the layout and visual viewports is a browser bar
// moving, not a keyboard.
const MIN_KEYBOARD_INSET = 120;

function keyboardInset(): number {
  const viewport = window.visualViewport;
  // No visualViewport, or the page is pinch-zoomed (which shrinks it with no
  // keyboard up): behave as if there is no keyboard.
  if (!viewport || viewport.scale > 1) return 0;
  const covered = Math.round(
    window.innerHeight - viewport.height - viewport.offsetTop,
  );
  return covered >= MIN_KEYBOARD_INSET ? covered : 0;
}

// Nested sheets share one lock: the body is released when the last one closes.
let scrollLocks = 0;
let scrollBeforeLock = "";

export const webCapabilities: PlatformCapabilities = {
  openExternalUrl(url: string) {
    window.open(url, "_blank");
  },

  printCurrentPage() {
    window.print();
  },

  async copyToClipboard(text: string) {
    await navigator.clipboard.writeText(text);
  },

  async readClipboard(): Promise<string> {
    return navigator.clipboard.readText();
  },

  pastedImage(paste: PasteContents): Blob | null {
    return paste.clipboardData
      ? firstPastedImage(paste.clipboardData.files)
      : null;
  },

  getShareOrigin(): string {
    return window.location.origin;
  },

  redirectTo(url: string) {
    window.location.href = url;
  },

  registerGlobalKeydownListener(handler: (event: GlobalKeyEvent) => void) {
    const listener = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      handler({
        key: event.key,
        hasModifier: event.metaKey || event.ctrlKey || event.altKey,
        hasShift: event.shiftKey,
        fromTextEntry: Boolean(
          target &&
          (target.tagName === "INPUT" ||
            target.tagName === "TEXTAREA" ||
            target.tagName === "SELECT" ||
            target.isContentEditable),
        ),
        preventDefault: () => event.preventDefault(),
      });
    };
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  },

  getCurrentPath(): string {
    return window.location.pathname;
  },

  downloadTextFile(
    filename: string,
    content: string,
    mimeType: string = "text/plain",
  ) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  },

  downloadBlob(filename: string, blob: Blob) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  },

  openContentInNewTab(content: string, mimeType: string) {
    openObjectUrlInNewTab(new Blob([content], { type: mimeType }));
  },

  openBlobInNewTab(blob: Blob) {
    openObjectUrlInNewTab(blob);
  },

  setVisibilityAwareInterval(callback: () => void, ms: number): () => void {
    const tick = () => {
      if (!document.hidden) {
        callback();
      }
    };
    const id = window.setInterval(tick, ms);
    return () => window.clearInterval(id);
  },

  getViewportSize() {
    return { width: window.innerWidth, height: window.innerHeight };
  },

  getStorageItem(key: string): string | null {
    try {
      return window.localStorage.getItem(key);
    } catch {
      // Storage access can throw under strict cookie/privacy modes.
      // Fail open: caller treats null as "no preference set".
      return null;
    }
  },

  setStorageItem(key: string, value: string | null): void {
    try {
      if (value === null) {
        window.localStorage.removeItem(key);
      } else {
        window.localStorage.setItem(key, value);
      }
    } catch {
      // Same rationale: failing to persist a UI preference is non-fatal.
    }
  },

  getSessionItem(key: string): string | null {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      // Same rationale as getStorageItem: fail open, the caller treats null as "nothing kept".
      return null;
    }
  },

  setSessionItem(key: string, value: string | null): void {
    try {
      if (value === null) {
        window.sessionStorage.removeItem(key);
      } else {
        window.sessionStorage.setItem(key, value);
      }
    } catch {
      // Same rationale as setStorageItem: failing to keep a UI choice is non-fatal.
    }
  },

  getDeviceCredential(key: string): string | null {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },

  setDeviceCredential(key: string, value: string | null): void {
    try {
      if (value === null) {
        window.localStorage.removeItem(key);
      } else {
        window.localStorage.setItem(key, value);
      }
    } catch {
      // Same rationale as setStorageItem: a failed write degrades to
      // "terminal not set up" on next boot rather than crashing the kiosk.
    }
  },

  warnBeforeUnload(active: boolean): () => void {
    if (!active) return () => {};
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  },

  matchesMediaQuery(query: string): boolean {
    return window.matchMedia(query).matches;
  },

  onMediaQueryChange(query: string, handler: () => void) {
    const list = window.matchMedia(query);
    list.addEventListener("change", handler);
    return () => list.removeEventListener("change", handler);
  },

  getKeyboardInset: keyboardInset,

  onKeyboardInsetChange(handler: () => void) {
    const viewport = window.visualViewport;
    if (!viewport) return () => {};
    viewport.addEventListener("resize", handler);
    viewport.addEventListener("scroll", handler);
    return () => {
      viewport.removeEventListener("resize", handler);
      viewport.removeEventListener("scroll", handler);
    };
  },

  // The staff app keeps its own title and icon: portal themes brand the customer portal only.
  setDocumentBranding() {},

  lockPageScroll() {
    scrollLocks += 1;
    if (scrollLocks === 1) {
      scrollBeforeLock = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    let isReleased = false;
    return () => {
      if (isReleased) return;
      isReleased = true;
      scrollLocks -= 1;
      if (scrollLocks === 0) document.body.style.overflow = scrollBeforeLock;
    };
  },
};
